import { useEffect, useMemo, useState } from 'react'
import { PageHeader } from '../../../components/PageHeader'
import { Avatar } from '../../../components/Avatar'
import { BackgroundTitle } from '../../../components/BackgroundTitle'
import { useAuth } from '../../../lib/auth'
import { useMutedIds, MUTED_PLACEHOLDER } from '../../../lib/api/mutes'
import { supabase } from '../../../lib/supabase'
import { readError } from '../../../lib/errors'
import {
  buildPromptText, fetchThreeStepWeeklyEntries, weekLabel,
  type ThreeStepWeeklyEntryRow, type ThreeStepWeeklyEventRow,
} from '../api/weekly'
import { useT } from '../../../i18n'

interface Props {
  eventId: string
  onBack: () => void
  /** Open the author's public profile when their name is tapped. */
  onOpenProfile?: (profileId: string) => void
}

type EntryWithAuthor = ThreeStepWeeklyEntryRow & {
  author_name: string | null
  author_avatar: string | null
}

/**
 * Read-only leaderboard for a finalized Three Step week. Top entry
 * is the champion, every entry is rank-ordered. Players can expand
 * any entry to read all three steps in full.
 */
export function ThreeStepWeeklyWinnersScreen({ eventId, onBack, onOpenProfile }: Props) {
  const t = useT()
  const { profile } = useAuth()
  const { mutedIds } = useMutedIds()
  const [event, setEvent] = useState<ThreeStepWeeklyEventRow | null>(null)
  const [entries, setEntries] = useState<EntryWithAuthor[]>([])
  const [expanded, setExpanded] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const evRes = await (supabase as any)
          .from('three_step_weekly_events').select('*').eq('id', eventId).single()
        if (evRes.error) throw evRes.error
        if (cancelled) return
        setEvent(evRes.data as ThreeStepWeeklyEventRow)

        // Same predicate as the voting screen: a finalized row with no
        // steps is a placeholder from a hub visit, not an entrant.
        const hasContent = (e: ThreeStepWeeklyEntryRow) =>
          (e.step1_text?.trim().length ?? 0) > 0 ||
          (e.step2_text?.trim().length ?? 0) > 0 ||
          (e.step3_text?.trim().length ?? 0) > 0
        const all = (await fetchThreeStepWeeklyEntries(eventId)).filter(e => e.is_final && hasContent(e))
        if (!cancelled) setEntries(all)
      } catch (e) {
        if (!cancelled) setError(readError(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [eventId])

  const sorted = useMemo(
    () => [...entries].sort((a, b) =>
      b.total_votes - a.total_votes || a.updated_at.localeCompare(b.updated_at),
    ),
    [entries],
  )

  return (
    <div className="screen">
      <BackgroundTitle text="Winners" color="#0EAE93" />
      <PageHeader
        title={t('Winners')}
        subtitle={event ? weekLabel(event.week_start) : '…'}
        onBack={onBack}
      />

      <div className="flex-1 px-5 py-4 space-y-3 overflow-y-auto pb-10 relative z-10">
        {event && (
          <div className="bg-game-three-step-light border-2 border-game-three-step-dark
                          rounded-2xl shadow-card p-3">
            <p className="text-[10px] font-semibold text-game-three-step-dark
                          uppercase tracking-widest mb-1">
              {t('The prompt')}
            </p>
            <p className="font-display text-base font-bold text-brand-text leading-snug">
              {buildPromptText(event.action_a, event.action_b, t)}
            </p>
          </div>
        )}

        {error && (
          <div className="card p-3 bg-brand-danger/10 border border-brand-danger/30">
            <p className="text-sm text-brand-danger">{error}</p>
          </div>
        )}

        {loading && (
          <>
            <div className="h-24 rounded-2xl bg-brand-elevated animate-pulse" />
            <div className="h-24 rounded-2xl bg-brand-elevated animate-pulse" />
          </>
        )}

        {!loading && sorted.length === 0 && (
          <div className="card p-5 text-center">
            <p className="font-semibold text-brand-text">{t('No entries this week')}</p>
            <p className="text-sm text-brand-muted mt-1">
              {t('Nobody finished a three-step solution for this prompt.')}
            </p>
          </div>
        )}

        {sorted.map((entry, i) => {
          const isOwn = entry.profile_id === profile?.id
          const muted = mutedIds.has(entry.profile_id)
          const isExpanded = expanded === entry.id
          const isWinner = i === 0 && entry.total_votes > 0
          const author = entry.author_name ?? 'Player'
          const ws1 = muted ? MUTED_PLACEHOLDER : (entry.step1_text || '…')
          const ws2 = muted ? MUTED_PLACEHOLDER : (entry.step2_text || '…')
          const ws3 = muted ? MUTED_PLACEHOLDER : (entry.step3_text || '…')
          return (
            <div key={entry.id}
                 className={`bg-game-three-step-light border-2 border-game-three-step-dark
                             rounded-2xl shadow-card overflow-hidden
                             ${isWinner ? 'ring-2 ring-brand-gold/50' : ''}`}>
              <button
                onClick={() => setExpanded(isExpanded ? null : entry.id)}
                className="w-full p-4 flex items-start gap-3 text-left"
              >
                <div className="shrink-0 w-8 text-center">
                  <span className={`font-display font-black text-xl
                    ${isWinner ? 'text-brand-gold' : 'text-game-three-step-dark'}`}>
                    {i + 1}
                  </span>
                </div>
                <Avatar name={author} avatarUrl={entry.author_avatar} size="md" className="shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    {onOpenProfile && !isOwn ? (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onOpenProfile(entry.profile_id) }}
                        className="font-semibold text-brand-text text-sm truncate
                                   underline underline-offset-2 hover:text-game-three-step-dark"
                      >
                        {author}
                      </button>
                    ) : (
                      <span className="font-semibold text-brand-text text-sm truncate">
                        {author}
                      </span>
                    )}
                    {isOwn && (
                      <span className="text-[10px] bg-white/70 text-game-three-step-dark
                                       px-2 py-0.5 rounded-full font-medium">
                        {t('You')}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-brand-muted line-clamp-2 mt-0.5">
                    {muted ? MUTED_PLACEHOLDER : (entry.step1_text || entry.step2_text || entry.step3_text || '…')}
                  </p>
                </div>
                <div className="shrink-0 flex flex-col items-center gap-0.5">
                  <span className="font-display font-bold text-game-three-step-dark text-lg">
                    {entry.total_votes}
                  </span>
                  <span className="text-[10px] text-brand-muted">votes</span>
                </div>
              </button>

              {isExpanded && (
                <div className="border-t border-game-three-step-dark/40 px-4 py-3 bg-white/50">
                  <ol className="space-y-2 text-sm text-brand-text">
                    <li className="flex gap-3">
                      <span className="font-display font-bold text-game-three-step-dark
                                       w-5 shrink-0">1.</span>
                      <span className="leading-relaxed whitespace-pre-wrap">
                        {ws1}
                      </span>
                    </li>
                    <li className="flex gap-3">
                      <span className="font-display font-bold text-game-three-step-dark
                                       w-5 shrink-0">2.</span>
                      <span className="leading-relaxed whitespace-pre-wrap">
                        {ws2}
                      </span>
                    </li>
                    <li className="flex gap-3">
                      <span className="font-display font-bold text-game-three-step-dark
                                       w-5 shrink-0">3.</span>
                      <span className="leading-relaxed whitespace-pre-wrap">
                        {ws3}
                      </span>
                    </li>
                  </ol>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
